/**
 * `info_screen` renderer — composes `@nextday/funnel-ui` `InfoScreen`.
 *
 * Pure presentation: hero (image / icon), heading + description, optional
 * bullet list. Has no answer to record. The CTA copy is part of `data` so
 * the orchestrator can label the global footer button — the renderer does
 * NOT own the footer.
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "info_screen",
 *     "data": {
 *       "headline": "Your SecretMate is almost ready.",
 *       "description": "...",
 *       "bullets": [{ "icon": "lock", "text": "Private chat" }, ...],
 *       "heroImageUrl": "https://...",
 *       "ctaLabel": "Personalize my match"
 *     }
 *   }
 */
import { InfoScreen } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";
import { urlOrTemplate } from "@survey-kit/urlOrTemplate";

const bulletSchema = z.object({
  /** Iconify icon name. Optional — the bullet renders without an icon if omitted. */
  icon: z.string().optional(),
  /** Bullet body. */
  text: z.string().min(1),
});

export const infoScreenSchema = z
  .object({
    headline: z.string().min(1),
    description: z.string().optional(),
    eyebrow: z.string().optional(),
    bullets: z.array(bulletSchema).optional(),
    /** Direct image URL. The §9 creative-slot resolver will populate this from `heroImageRef` upstream. */
    heroImageUrl: urlOrTemplate.optional(),
    /** Optional ref into the brand's R2 creative bucket; resolved upstream. */
    heroImageRef: z.string().optional(),
    /** Full-bleed background image with vignette gradient. */
    backgroundImageUrl: urlOrTemplate.optional(),
    /** WebM video URL for full-bleed background. Uses backgroundImageUrl as poster. */
    backgroundVideoUrl: urlOrTemplate.optional(),
    /** Small text rendered below the CTA area. */
    footnote: z.string().optional(),
    /** Vertical positioning of text overlay in full-bleed layout ("top" or "bottom"). */
    textPosition: z.enum(["top", "bottom"]).optional(),
    /**
     * CTA copy the orchestrator forwards to its global `<ContinueButton>`.
     * Stored on `data` (not on the renderer's tree) so a single global footer
     * remains the single source of truth for advance behavior.
     */
    ctaLabel: z.string().min(1).optional(),
  })
  .strict();

export type InfoScreenData = z.infer<typeof infoScreenSchema>;

const InfoScreenRenderer = ({ data, step, onAdvance }: RendererProps<InfoScreenData>) => (
  <InfoScreen
    headline={data.headline}
    description={data.description}
    eyebrow={data.eyebrow}
    bullets={data.bullets}
    heroImageUrl={data.heroImageUrl}
    backgroundImageUrl={data.backgroundImageUrl}
    backgroundVideoUrl={data.backgroundVideoUrl}
    footnote={data.footnote}
    textPosition={data.textPosition}
    onPress={onAdvance}
    testId={`renderer-info-screen-${step.id}`}
  />
);

export const infoScreenRenderer = createRenderer({
  type: "info_screen",
  dataSchema: infoScreenSchema,
  label: "Info screen",
  Component: InfoScreenRenderer,
});

export default infoScreenRenderer;
